import {
  Box, Typography, TextField, Button, Paper, Grid,
  CircularProgress, IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Formulario compartido de datos personales + CV del candidato.
 * Componente CONTROLADO: el estado (profile, cvFile, cvUrl, uploadingCv) vive
 * en el padre. Lo usan PortalApplicationScreen (postulación) y
 * PortalProfileScreen (edición de perfil desde "Mi perfil").
 */
const composeFullName = (names, firstLastName, secondLastName) => {
  const clean = (v) => String(v || '').trim().replace(/\s+/g, ' ');
  const parts = [clean(names), clean(firstLastName), clean(secondLastName)].filter(Boolean);
  return parts.join(' ');
};

const PortalProfileForm = ({
  profile,
  onProfileChange,
  candidateData,
  showIdentity = false,
  cvFile,
  cvUrl,
  uploadingCv,
  onCvChange,
  onClearCvFile,
}) => {
  const { t } = useTranslation();

  // Editar una parte recompone name (los padres usan updates funcionales,
  // por lo que las dos llamadas seguidas son seguras). Si las partes quedan
  // vacías, name conserva su valor actual (perfiles legados).
  const handleNamePart = (field, value) => {
    onProfileChange(field, value);
    const next = { ...profile, [field]: value };
    const composed = composeFullName(next.names, next.firstLastName, next.secondLastName);
    if (composed) onProfileChange('name', composed);
  };

  return (
    <>
      {/* Identidad verificada (solo lectura) — pantalla Mi perfil */}
      {showIdentity && (candidateData?.email || candidateData?.rut) && (
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            {t('portal.profile.identityTitle')}
          </Typography>
          <Grid container spacing={2}>
            {candidateData?.email && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('portal.emailLabel')}
                  value={candidateData.email}
                  fullWidth
                  disabled
                  helperText={t('portal.profile.identityHint')}
                />
              </Grid>
            )}
            {candidateData?.rut && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('portal.rut')}
                  value={candidateData.rut}
                  fullWidth
                  disabled
                  helperText={t('portal.rutVerified')}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Datos personales */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {t('portal.personalData')}
        </Typography>
        <Grid container spacing={2}>
          {/* Nombre por PARTES (regla global): name se compone desde ellas.
              Perfil legado sin partes: name actual queda visible de referencia
              y solo cambia cuando la persona llena las partes. */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('portal.names')}
              value={profile.names || ''}
              onChange={(e) => handleNamePart('names', e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('portal.firstLastName')}
              value={profile.firstLastName || ''}
              onChange={(e) => handleNamePart('firstLastName', e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('portal.secondLastName')}
              value={profile.secondLastName || ''}
              onChange={(e) => handleNamePart('secondLastName', e.target.value)}
              fullWidth
              helperText={t('portal.secondLastNameHint')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('portal.fullNamePreview')}
              value={profile.name || ''}
              fullWidth
              disabled
              helperText={t('portal.fullNameHint')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('portal.phone')}
              value={profile.phone}
              onChange={(e) => onProfileChange('phone', e.target.value)}
              fullWidth
            />
          </Grid>
          {/* El documento verificado se muestra en la sección de identidad
              cuando showIdentity; aquí solo si aún no está verificado */}
          {!showIdentity && (
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('portal.rut')}
                value={profile.rut}
                onChange={(e) => onProfileChange('rut', e.target.value)}
                fullWidth
                // El documento verificado al ingresar es identificador: no editable
                disabled={!!candidateData?.rut}
                helperText={candidateData?.rut ? t('portal.rutVerified') : ''}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('portal.country')}
              value={profile.country}
              onChange={(e) => onProfileChange('country', e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('portal.profession')}
              value={profile.profession}
              onChange={(e) => onProfileChange('profession', e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('portal.currentPosition')}
              value={profile.currentPosition}
              onChange={(e) => onProfileChange('currentPosition', e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('portal.salarioPretendido')}
              value={profile.salarioPretendido}
              onChange={(e) => onProfileChange('salarioPretendido', e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* CV */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {t('portal.cv')}
        </Typography>

        {cvUrl && !cvFile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FileIcon fontSize="small" color="primary" />
            <Typography variant="body2" color="primary">{t('portal.cvUploaded')}</Typography>
            <Button size="small" href={cvUrl} target="_blank" rel="noopener noreferrer">
              {t('portal.cvView')}
            </Button>
          </Box>
        )}

        {cvFile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {uploadingCv ? <CircularProgress size={16} /> : <FileIcon fontSize="small" color="success" />}
            <Typography variant="body2">{cvFile.name}</Typography>
            {!uploadingCv && (
              <IconButton size="small" onClick={onClearCvFile}>
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

        <Button
          component="label"
          variant="outlined"
          startIcon={uploadingCv ? <CircularProgress size={16} /> : <UploadIcon />}
          disabled={uploadingCv}
          size="small"
        >
          {cvFile || cvUrl ? t('portal.cvReplace') : t('portal.cvUpload')}
          <input type="file" hidden accept=".pdf,.doc,.docx" onChange={onCvChange} />
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('portal.cvHint')}
        </Typography>
      </Paper>
    </>
  );
};

export default PortalProfileForm;
